<script setup lang="ts">
import { useForm } from 'vee-validate';
import { computed, onBeforeMount, ref, watch, watchEffect } from 'vue';

import api from '@/api';
import type { AdditionalFundsRequiredDto, ListAdditionalFundsPurposes } from '@/api/backend/public';
import { defaultAddress, defaultAdditionalFundRequired, defaultIncomingProperty } from '@/helpers/builder';
import { AdditionalFundsPurpose, PropertyType, StampDutyStatus, PropertyToFundsPurposeMap } from '@/helpers/const';
import { snakeCase } from '@/helpers/format';
import { useCalcApi } from '@/helpers/useCalcApi';
import { useCustomerQuoteProcess } from '@/helpers/useCustomerQuoteProcess';

import IncomingPropertyAddress from '@/components/application/incoming-property-address.vue';
import IncomingPropertyDepositType from '@/components/application/incoming-property-deposit-type.vue';
import IncomingPropertyEstimatedDepositAmount from '@/components/application/incoming-property-estimated-deposit-amount.vue';
import IncomingPropertyEstimatedSettlementDate from '@/components/application/incoming-property-estimated-settlement-date.vue';
import IncomingPropertyEstimatedPurchasePrice from '@/components/application/incoming-property-estimated-purchase-price.vue';
import IncomingPropertyPropertyPurpose from '@/components/application/incoming-property-property-purpose.vue';
import IncomingPropertyReceiveRentalIncome from '@/components/application/incoming-property-receive-rental-income.vue';
import IncomingPropertyExpectedRentalIncome from '@/components/application/incoming-property-expected-rental-income.vue';
import IncomingPropertyStampDuty from '@/components/application/incoming-property-stamp-duty.vue';
import IncomingPropertyType from '@/components/application/incoming-property-type.vue';
import IncomingPropertyTransferFeeCovered from '@/components/application/incoming-property-transfer-fee-covered.vue';
import { incomingPropertySchemaV2 } from '@/validation/schemas/v2/incoming-property';
import { incomingPropertySimpleSchema } from '@/validation/schemas/simple/incoming-property';

import SimpleIncomingPropertyAddress from './modules/IncomingProperties/SimpleIncomingPropertyAddress.vue';
// import SimpleComprehensive from './modules/SimpleComprehensive.vue';

import type { AdditionalFundsRequiredProperty } from '@/types/AdditionalFundsRequiredProperty';
import type { Address, IncomingProperty, IncomingPropertyEntry } from '@/types';

type IncomingPropertyWithSettlementDate = IncomingPropertyEntry & { settlementDate: string };

let propertyCounter = 0;
let listAdditionalFundsPurposes: ListAdditionalFundsPurposes = [];

const initAddress: Address = {
  addressLine: '',
  locality: '',
  state: '',
  postcode: '',
  countryCode: 'AU',
};

const { loanApp, sectionStates, saveAndGoNext, send } = useCustomerQuoteProcess();
const calc = useCalcApi();

const toggle = ref(true);
const isListReady = ref(false);

const incomingPropertiesType = computed(() => sectionStates.value.incomingProperties);

const validationSchema = computed(() => {
  if (incomingPropertiesType.value === 'simple') {
    return incomingPropertySimpleSchema;
  } else {
    return incomingPropertySchemaV2;
  }
});

const {
  values,
  setFieldValue,
  meta,
  submitCount,
  handleSubmit: validateSubmit,
  resetForm,
} = useForm<IncomingPropertyWithSettlementDate>({
  validationSchema,
  initialValues: loanApp.value.incomingProperties.length
    ? { ...loanApp.value.incomingProperties[0] }
    : {
        id: `incoming-property-${propertyCounter++}`,
        contractForSale: false,
        address: incomingPropertiesType.value === 'simple' ? initAddress : defaultAddress(),
      },
  validateOnMount: false,
});

watch(incomingPropertiesType, (newType) => {
  if (newType === 'simple') {
    setFieldValue('address', initAddress);
  } else {
    setFieldValue('address', defaultAddress());
  }
});

watchEffect(() => {
  const isRetirementProperty = values.propertyType === PropertyType.RETIREMENT_LAND_OR_COMMUNITY_HOME;
  const hasInvalidStampDutyStatus = !values.contractForSale && values.stampDutyStatus === StampDutyStatus.ALREADY_PAID;

  if (isRetirementProperty || hasInvalidStampDutyStatus) {
    setFieldValue('stampDutyStatus', undefined);
  }
});

onBeforeMount(async () => {
  isListReady.value = false;
  try {
    listAdditionalFundsPurposes = await api.backend.public.listAdditionalFundsPurposes();
  } finally {
    isListReady.value = true;
  }
});

if (loanApp.value.incomingProperties.length) {
  const [incomingProperty] = loanApp.value.incomingProperties;
  const isRetirementOrVacant =
    incomingProperty.propertyType === PropertyType.RETIREMENT_LAND_OR_COMMUNITY_HOME ||
    incomingProperty.propertyType === PropertyType.VACANT_LAND;

  const purchaseAmount =
    isRetirementOrVacant && loanApp.value.additionalFundsRequiredProperties?.purchasePrice
      ? loanApp.value.additionalFundsRequiredProperties.purchasePrice
      : incomingProperty.purchaseAmount;

  resetForm({
    values: {
      ...incomingProperty,
      purchaseAmount,
    },
  });
}

const handleSubmit = validateSubmit(async (values) => {
  const property: IncomingProperty = Object.assign(defaultIncomingProperty(), values);
  if (
    values.propertyType === PropertyType.RETIREMENT_LAND_OR_COMMUNITY_HOME ||
    values.propertyType === PropertyType.VACANT_LAND
  ) {
    const additionalFundsPurposeId = listAdditionalFundsPurposes?.find(
      (item) => snakeCase(item.title) === PropertyToFundsPurposeMap[values.propertyType ?? PropertyType.VACANT_LAND],
    )?.id;
    send({
      type: 'UPDATE',
      loanApp: {
        ...loanApp.value,
        loanType: 'single_security',
      },
    });

    const additionalFundsRequired: AdditionalFundsRequiredDto = Object.assign(defaultAdditionalFundRequired(), {
      fundsRequiredDate: values.settlementDate,
      fundsRequiredAmount: 0,
      additionalFundsPurposeId: additionalFundsPurposeId ?? '',
    });

    const additionalFundsRequiredProperties: AdditionalFundsRequiredProperty = Object.assign(
      {},
      {
        isThereContractForSale: values.contractForSale,
        isAdditionalFundsRequired: true,
        reasonForFundsOption:
          property.propertyType === PropertyType.RETIREMENT_LAND_OR_COMMUNITY_HOME
            ? AdditionalFundsPurpose.PURCHASE_RETIREMENT_LAND_LEASE_OR_COMMUNITY_HOME
            : AdditionalFundsPurpose.PURCHASE_VACANT_LAND,
        ...property.address, // we ended up having two addresses for this type for cross compatibility so this type can be used for incoming property and loan purpose entry/form when applying for a single security
        propertyAddress: values.address ?? defaultAddress(),
        contributingFundsTowardsPurchase: false,
        fundsToContributeAmount: 0,
        propertyType: property.propertyType,
        propertyPurpose: property.propertyPurpose,
        settlementDate: property.settlementDate ?? '',
        purchasePrice: property.purchaseAmount ?? 0,
        depositAmount: property.depositAmount ?? 0,
        depositType: property.depositType ?? 'cash_payment',
        stampDutyStatus: property.stampDutyStatus ?? 'already_paid',
        transferFeeCovered: property.transferFeeCovered ?? false,
        description: '',
        fundsRequiredDate: additionalFundsRequired.fundsRequiredDate,
        fundsRequiredAmount: additionalFundsRequired.fundsRequiredAmount,
        additionalFundsPurposeId: additionalFundsRequired.additionalFundsPurposeId,
      },
    );

    saveAndGoNext({
      additionalFundsRequired,
      incomingProperties: [property],
      additionalFundsRequiredProperties,
    });
  } else {
    send({
      type: 'UPDATE',
      loanApp: {
        ...loanApp.value,
        loanType: 'buy_now',
      },
    });

    /*
     * If single security property saved and then customer changes incoming property,
     * it will still have fundsRequired. This needs to be removed when saving buy now property.
     */
    delete loanApp.value.additionalFundsRequired;
    delete loanApp.value.additionalFundsRequiredProperties;

    property.stampDuty = await calc.getStampDuty(property);
    loanApp.value.settlementDate = values.settlementDate;
    saveAndGoNext({ incomingProperties: [property] });
  }
});
</script>

<template>
  <div>
    <div class="mb-4 pb-2">
      <h2 class="text-2xl font-medium">
        Tell us about the property you are
        <BxHighlight>buying</BxHighlight>?
      </h2>
    </div>
    <form @submit="handleSubmit">
      <div>
        <!-- <div class="my-4">
          <SimpleComprehensive :section-key="'incomingProperties'" title="Buying" />
          <p class="pt-4">
            Use the Quick vs Comprehensive toggle to choose whether you want to enter just the postcode area or the full
            property address.
          </p>
        </div> -->
        <BxExpander v-model="toggle" separator>
          <template #header>
            <div class="px-4 py-5 sm:px-6">
              <div class="flex flex-wrap items-center justify-between sm:flex-nowrap">
                <h3 class="flex items-center text-lg font-medium leading-6 text-gray-900">
                  <span class="mr-3 hidden md:block"><BxIcon name="incomingProperty" /></span>
                  <span>Property details</span>
                </h3>
              </div>
            </div>
          </template>
          <template #body>
            <div class="p-6">
              <!-- if there is no contract for sale but the user has entered with the 'noContractOfSale' parameter-->
              <div class="no-contract">
                <div class="incoming-margin-top">
                  <IncomingPropertyType :id="values.id" />
                  <SimpleIncomingPropertyAddress v-if="incomingPropertiesType === 'simple'" :id="values.id" />
                  <IncomingPropertyAddress v-if="incomingPropertiesType === 'comprehensive'" :id="values.id" />
                  <div v-if="values.propertyType" class="incoming-margin-top">
                    <div
                      v-if="values.propertyType === PropertyType.RETIREMENT_LAND_OR_COMMUNITY_HOME"
                      class="incoming-margin-top"
                    >
                      <IncomingPropertyEstimatedSettlementDate :id="values.id" />
                      <IncomingPropertyEstimatedPurchasePrice :id="values.id" />
                      <IncomingPropertyEstimatedDepositAmount :id="values.id" />
                      <IncomingPropertyDepositType :id="values.id" :contract-for-sale="false" />
                      <IncomingPropertyTransferFeeCovered :id="values.id" />
                    </div>
                    <div v-else-if="values.propertyType === PropertyType.VACANT_LAND" class="incoming-margin-top">
                      <IncomingPropertyPropertyPurpose :id="values.id" />
                      <IncomingPropertyEstimatedSettlementDate :id="values.id" />
                      <IncomingPropertyEstimatedPurchasePrice :id="values.id" />
                      <IncomingPropertyEstimatedDepositAmount :id="values.id" />
                      <IncomingPropertyDepositType :id="values.id" :contract-for-sale="false" />
                      <IncomingPropertyStampDuty :id="values.id" :contract-for-sale="false" />
                      <IncomingPropertyTransferFeeCovered :id="values.id" />
                    </div>
                    <!-- residential, rural residential, off-plan residential -->
                    <div v-else>
                      <IncomingPropertyPropertyPurpose :id="values.id" />
                      <IncomingPropertyReceiveRentalIncome :id="values.id" />
                      <IncomingPropertyExpectedRentalIncome v-if="values.receiveRentalIncome" :id="values.id" />
                      <IncomingPropertyEstimatedSettlementDate :id="values.id" />
                      <IncomingPropertyEstimatedPurchasePrice :id="values.id" />
                      <IncomingPropertyEstimatedDepositAmount :id="values.id" />
                      <IncomingPropertyDepositType :id="values.id" :contract-for-sale="false" />
                      <IncomingPropertyStampDuty :id="values.id" :contract-for-sale="false" />
                      <IncomingPropertyTransferFeeCovered :id="values.id" />
                    </div>
                  </div>
                </div>
              </div>
              <BxErrorPanel v-show="!meta.valid && submitCount > 0" class="mt-6">
                Please review the error messages above.
              </BxErrorPanel>
              <div class="mt-6">
                <BxButton id="incoming-properties-continue" variant="primary" type="submit">Save and continue</BxButton>
              </div>
            </div>
          </template>
        </BxExpander>
      </div>
    </form>
  </div>
</template>

<style lang="scss" scoped>
.section-panel {
  --border-radius: 0.5rem;
  --border-color: #ccc;

  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);

  background-color: #fff;
}

.no-contract {
  margin-top: 1.5rem;
  margin: 17px 16px 17px 0px;
}

.no-contract-info {
  border-radius: 8px 8px 0px 0px;
  background-color: white;
  padding: 15px;
}

.no-contract-header {
  border-radius: 8px 8px 0px 0px;
  background-color: #cccccc;
  display: flex;
  gap: 10px;
  padding: 15px;

  p {
    margin-top: 3px;
  }
}

.no-contract-buttons {
  display: flex;
  gap: 15px;
}

.incoming-margin-top {
  margin-top: 1.5rem;
}

.link {
  @apply cursor-pointer text-bridgit-navy underline hover:text-bridgit-vibrantGreen;
}

.schedule-buttons {
  width: 100%;
  text-align: right;
}
</style>
